import React, { FC, useState } from "react";
import styled from "@emotion/styled";
import { TextField } from "@radix-ui/themes";
import { Button } from "@/components/button";
import {
  AudioRecordingUploadStatus,
  AudioRecordingStatus,
  AudioRecordingProcessingStatus,
} from "@/pages/audio-recordings/shared/api";

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "All" },
  { value: "INITIAL", label: "Initial" },
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "COMPLETED", label: "Completed" },
  { value: "ERROR", label: "Error" },
];

export interface AudioRecordingsSearchParams {
  query: string;
  spaceAccountId: string;
  uploadStatus: AudioRecordingUploadStatus | "";
  recordingStatus: AudioRecordingStatus | "";
  processingStatus: AudioRecordingProcessingStatus | "";
}

interface SearchFormProps {
  initialValues: AudioRecordingsSearchParams;
  onSearch: (params: AudioRecordingsSearchParams) => void;
}

export const AudioRecordingsSearchForm: FC<SearchFormProps> = ({ initialValues, onSearch }) => {
  const [query, setQuery] = useState(initialValues.query);
  const [spaceAccountId, setSpaceAccountId] = useState(initialValues.spaceAccountId);
  const [uploadStatus, setUploadStatus] = useState<AudioRecordingUploadStatus | "">(
    initialValues.uploadStatus
  );
  const [recordingStatus, setRecordingStatus] = useState<AudioRecordingStatus | "">(
    initialValues.recordingStatus
  );
  const [processingStatus, setProcessingStatus] = useState<AudioRecordingProcessingStatus | "">(
    initialValues.processingStatus
  );

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSearch({
      query: query.trim(),
      spaceAccountId: spaceAccountId.trim(),
      uploadStatus,
      recordingStatus,
      processingStatus,
    });
  };

  const handleClear = () => {
    setQuery("");
    setSpaceAccountId("");
    setUploadStatus("");
    setRecordingStatus("");
    setProcessingStatus("");
    onSearch({
      query: "",
      spaceAccountId: "",
      uploadStatus: "",
      recordingStatus: "",
      processingStatus: "",
    });
  };

  return (
    <FormContainer onSubmit={handleSubmit}>
      <Row>
        <TextFieldContainer
          placeholder="Search by ID or title..."
          value={query}
          onChange={event => setQuery(event.currentTarget.value)}
          maxLength={100}
          aria-label="Search Audio Recordings"
        />
        <TextFieldContainer
          placeholder="Space Account ID (UUID)"
          value={spaceAccountId}
          onChange={event => setSpaceAccountId(event.currentTarget.value)}
          maxLength={36}
          aria-label="Filter by Space Account ID"
        />
      </Row>
      <Row>
        <FilterGroup>
          <FilterLabel>Upload:</FilterLabel>
          <Select
            value={uploadStatus}
            onChange={e => setUploadStatus(e.target.value as AudioRecordingUploadStatus | "")}
          >
            {STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </FilterGroup>
        <FilterGroup>
          <FilterLabel>Recording:</FilterLabel>
          <Select
            value={recordingStatus}
            onChange={e => setRecordingStatus(e.target.value as AudioRecordingStatus | "")}
          >
            {STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </FilterGroup>
        <FilterGroup>
          <FilterLabel>Processing:</FilterLabel>
          <Select
            value={processingStatus}
            onChange={e =>
              setProcessingStatus(e.target.value as AudioRecordingProcessingStatus | "")
            }
          >
            {STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </FilterGroup>
        <Button type="submit">Search</Button>
        <Button type="button" variant="soft" onClick={handleClear}>
          Clear
        </Button>
      </Row>
    </FormContainer>
  );
};

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const TextFieldContainer = styled(TextField.Root)`
  max-width: 280px;
  min-width: 200px;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
`;

const FilterLabel = styled.span`
  font-size: 13px;
  color: var(--gray-11);
`;

const Select = styled.select`
  padding: 6px 8px;
  border: 1px solid var(--gray-6);
  border-radius: 4px;
  font-size: 13px;
  background: var(--gray-1);
  min-width: 100px;
`;
